import { Packet } from "../Interfaces/commonDtos";
import { AxiosRequestConfig } from "axios";
import { User } from "../Interfaces/connections";
import { Report } from "../Interfaces/TableInterfaces";
import {
  downloadExcel,
  getTable,
  postRequest,
  getRequest,
  putRequest,
  deleteRequest,
  postDownloadExcel,
  getDownloadExcel,
} from "./requestTemplates";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  InstrumentBubbleSearchParams,
  IInstrumentBubbleTableReport,
  PurchaseSearchParams,
  IPurchaseTableReport,
  InstrumentDomainSearchParams,
  IInstrumentDomainTableReport,
  PairConfigTableReport,
  PairConfigSearchParams,
  IConfig,
  IExecutionSettings,
  ExecutionStatusTableReport,
  ExecutionStatusSearchParams,
  GoldTaskPortfolio,
  GoldPositionTableReport,
  GoldPositionSearchParams,
  TriggeredPositionsTableReport,
  TriggeredPositionsSearchParams,
  GoldPositionsOrderTableReport,
  GoldPositionsOrderSearchParams,
  UserGoldInstrumentTaskTableReport,
  UserGoldInstrumentTaskSearchParams,
  UserGoldInstrumentTaskUpsertDto,
  GoldPositionWatchListSearchParams,
  GoldPositionWatchListTableReport,
  GoldTriggerDto,
  GoldPositionChart,
  IInstrumentBubbleGraph,
  IInstrumentBubbleDailyGraph,
  IInstrumentBubbleCurrentGraph,
  FundCompositionSearchParams,
  IFundCompositionTableReport,
  IFundCompositionChartRequest,
  IFundCancelNavDailyGraph,
} from "src/Interfaces/goldArbitrage";
//----------------محاسبات صندوق----------------

/**
 * gold arbitrage => cash calculations
 * دراپداون دارایی پایه صندوق
 *
 * @param {number} userId
 * @async
 * @version 1.4.0
 */
export const getCashAssetDropdown = (): Promise<DropdownItem[]> =>
  getRequest(`/v1/gold/GetInstrumentDropdown`);

/**
 * gold arbitrage => cash calculations
 * جدول
 *
 * @param {InstrumentBubbleSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getGoldArbitrageTable = (
  searchParam: InstrumentBubbleSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IInstrumentBubbleTableReport>> =>
  getTable<InstrumentBubbleSearchParams, IInstrumentBubbleTableReport>(
    "/v1/gold/instrumentBubble",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => cash calculations
 * جدول خرید و فروش
 *
 * @param {PurchaseSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPurchaseTable = (
  searchParam: PurchaseSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPurchaseTableReport>> =>
  getTable<PurchaseSearchParams, IPurchaseTableReport>(
    "/v1/gold/instrumentBubble/current",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => cash calculations
 * جدول دامنه صندوق
 *
 * @param {InstrumentDomainSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getInstrumentDomainTable = (
  searchParam: InstrumentDomainSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IInstrumentDomainTableReport>> =>
  getTable<InstrumentDomainSearchParams, IInstrumentDomainTableReport>(
    "/v1/gold/instrumentDomain",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => cash calculations
 *جدول مرز اجرا
 *
 * @param {PairConfigSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPairConfigTable = (
  searchParam: PairConfigSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<PairConfigTableReport>> =>
  getTable<PairConfigSearchParams, PairConfigTableReport>(
    "/v1/gold/instrumentBubble/pairConfig",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => cash calculations
 * تنظیمات
 *
 * @async
 * @version 1.4.0
 */
export const getConfig = (): Promise<IConfig> =>
  getRequest(`/v1/gold/settings`);

/**
 * gold arbitrage => cash calculations
 * ثیت تنظیمات
 *
 * @param {IMarginConfig} data
 * @async
 * @version 1.4.0
 */
export const setConfig = (config: IConfig): Promise<Packet<boolean>> =>
  postRequest(`/v1/gold/settings`, config);

/**
 * gold arbitrage => cash calculations
 * نمودار میانگین n روزه حباب
 *
 * @param {IInstrumentBubbleGraph} data
 * @async
 * @version 1.4.0
 */
export const instrumentBubbleGraph = (
  config: IInstrumentBubbleDailyGraph,
): Promise<Graph_Dto> =>
  postRequest(`/v1/gold/instrumentBubble/chart/daily`, config);

/**
 * gold arbitrage => cash calculations
 * حباب تعدیلی
 *
 * @param {IInstrumentBubbleGraph} data
 * @async
 * @version 1.4.0
 */
export const instrumentNormalizedBubbleGraph = (
  config: IInstrumentBubbleGraph,
): Promise<Graph_Dto> =>
  postRequest(`/v1/gold/instrumentNormalizedBubble/chart/hourly`, config);

/**
 * gold arbitrage => cash calculations
 * حباب تعدیلی محاسباتی
 *
 * @param {IInstrumentBubbleGraph} data
 * @async
 * @version 1.4.0
 */
export const instrumentCalculatedNormalizedBubbleGraph = (
  config: IInstrumentBubbleGraph,
): Promise<Graph_Dto> =>
  postRequest(`/v1/gold/instrumentCalculatedNormalizedBubble/chart/hourly`, config);
/**
 * gold arbitrage => cash calculations
 * نمودار میان‌روز % اختلاف NAV محاسباتی و NAV TSE
 *
 * @param {IInstrumentBubbleGraph} data
 * @async
 * @version 1.4.0
 */
export const instrumentNavDifferenceGraph = (
  config: IInstrumentBubbleGraph,
): Promise<Graph_Dto> =>
  postRequest(`/v1/gold/instrumentNavDifference/chart/hourly`, config);

export const instrumentNavComparisonGraph = (
  config: IInstrumentBubbleGraph,
): Promise<Graph_Dto[]> =>
  postRequest(`/v1/gold/instrumentNavComparison/chart/hourly`, config);

export const instrumentNavRefitGraph = (
  config: IInstrumentBubbleGraph,
): Promise<Graph_Dto[]> =>
  postRequest(`/v1/gold/instrumentNavRefit/chart/hourly`, config);

export const instrumentNavRefitDelayGraph = (
  config: IInstrumentBubbleGraph,
): Promise<Graph_Dto[]> =>
  postRequest(`/v1/gold/instrumentNavRefitDelay/chart/hourly`, config);
/**
 * gold arbitrage => cash calculations
 * حباب تعدیلی
 *
 * @param {IInstrumentBubbleCurrentGraph} data
 * @async
 * @version 1.4.0
 */
export const instrumentBubbleCurrentGraph = (
  config: IInstrumentBubbleCurrentGraph,
): Promise<Graph_Dto> =>
  postRequest(`/v1/gold/instrumentBubbleCurrent/chart/hourly`, config);

//----------------ترکیب دارایی صندوق----------------

/**
 * gold arbitrage => fund composition
 * جدول ترکیب دارایی صندوق
 *
 * @param {FundCompositionSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getFundCompositionTable = (
  searchParam: FundCompositionSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IFundCompositionTableReport>> =>
  getTable<FundCompositionSearchParams, IFundCompositionTableReport>(
    "/v1/gold/fundComposition",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => fund composition
 * نمودار تاریخی ترکیب دارایی صندوق (از دو ماه قبل)
 *
 * @param {IFundCompositionChartRequest} data
 * @async
 * @version 1.4.0
 */
export const fundCompositionGraph = (
  data: IFundCompositionChartRequest,
): Promise<Graph_Dto[]> =>
  postRequest(`/v1/gold/fundComposition/chart/monthly`, data);

/**
 * gold arbitrage => fund composition
 * نمودار تاریخی NAV ابطال
 *
 * @param {IFundCancelNavDailyGraph} data
 * @async
 * @version 1.4.0
 */
export const fundCancelNavGraph = (
  data: IFundCancelNavDailyGraph,
): Promise<Graph_Dto[]> =>
  postRequest(`/v1/gold/fundComposition/cancelNav/chart/daily`, data);

//---------------------------------وضعیت اجرا---------------------------------

/**
 * gold arbitrage => ExecutionStatus
 * جدول وضعیت اجرا
 *
 * @param {ExecutionStatusSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getExecutionStatusTable = (
  searchParam: ExecutionStatusSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ExecutionStatusTableReport>> =>
  getTable<ExecutionStatusSearchParams, ExecutionStatusTableReport>(
    "/v1/gold/executionStatus",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getGoldTaskPortfolio = (
  taskId: number,
  axiosOptions?: AxiosRequestConfig,
): Promise<GoldTaskPortfolio> =>
  getRequest(`/v1/gold/userGoldInstrumentTask/${taskId}/portfolio`, axiosOptions);
/**
 * gold arbitrage => ExecutionStatus
 * جدول  سمت چپ
 *
 * @param {ExecutionStatusSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getGoldPositionTable = (
  searchParam: GoldPositionSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<GoldPositionTableReport>> =>
  getTable<GoldPositionSearchParams, GoldPositionTableReport>(
    "/v1/gold/goldPositions",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => ExecutionStatus
 * جدول موقعیت های فعال شده
 *
 * @param {TriggeredPositionsSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getTriggeredPositionsTable = (
  searchParam: TriggeredPositionsSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<TriggeredPositionsTableReport>> =>
  getTable<TriggeredPositionsSearchParams, TriggeredPositionsTableReport>(
    "/v1/gold/triggeredPositions",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => ExecutionStatus
 * جدول سفارش های موقعیت طلا
 *
 * @param {GoldPositionsOrderSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getGoldPositionsOrderTable = (
  searchParam: GoldPositionsOrderSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<GoldPositionsOrderTableReport>> =>
  getTable<GoldPositionsOrderSearchParams, GoldPositionsOrderTableReport>(
    "/v1/gold/goldPositionsOrder",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    { timeout: 30000, ...axiosOptions },
  );

/**
 * gold arbitrage => ExecutionStatus
 * ارسال دستی موقعیت طلا (تایید مودال ارسال دستی)
 *
 * @param {GoldTriggerDto} dto
 * @async
 * @version 1.4.0
 */
export const triggerGoldPosition = (
  dto: GoldTriggerDto,
): Promise<Packet<boolean>> => postRequest("/v1/gold/trigger", dto);

/**
 * gold arbitrage => ExecutionStatus
 * گراف ساعت
 *
 * @param {GoldPositionChart} dto
 * @async
 * @version 1.4.0
 */
export const getHourGraph = async (
  accountId?: number,
): Promise<Array<Graph_Dto>> =>
  postRequest("/v1/gold/goldPositions/chart/hourly", { accountId });

/**
 * gold arbitrage => ExecutionStatus
 * گراف روزانه
 *
 * @param {GoldPositionChart} dto
 * @async
 * @version 1.4.0
 */
export const getDayGraph = async (
  accountId?: number,
): Promise<Array<Graph_Dto>> =>
  postRequest("/v1/gold/goldPositions/chart/daily", { accountId });

export const getPairConfigExcel = () => {
  const rand = Math.random();
  return getDownloadExcel(`/v1/gold/instrumentBubble/pairConfig/excel?${rand}`);
};

export const uploadPairConfigExcel = (
  file: FormData,
): Promise<Packet<boolean>> =>
  postRequest(`/v1/gold/instrumentBubble/pairConfig/UploadExcel`, file);

// ---------------------------مدیریت اجرا----------------------------
/**
 * gold arbitrage => ExecutionStatus
 * جدول تسک ابزار طلای کاربر
 *
 * @param {UserGoldInstrumentTaskSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getUserGoldInstrumentTaskTable = (
  searchParam: UserGoldInstrumentTaskSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<UserGoldInstrumentTaskTableReport>> =>
  getTable<
    UserGoldInstrumentTaskSearchParams,
    UserGoldInstrumentTaskTableReport
  >(
    "/v1/gold/userGoldInstrumentTask",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * gold arbitrage => ExecutionManagement
 * حذف تسک ابزار طلای کاربر
 *
 * @param {number} id
 * @async
 * @version 1.4.0
 */
export const deleteUserGoldInstrumentTask = (
  id: number,
): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/gold/userGoldInstrumentTask?id=${id}`);

/**
 * gold arbitrage => ExecutionManagement
 * ثبت / ویرایش تسک ابزار طلای کاربر
 *
 * @param {UserGoldInstrumentTaskUpsertDto} dto
 * @async
 * @version 1.4.0
 */
export const upsertUserGoldInstrumentTask = (
  dto: UserGoldInstrumentTaskUpsertDto,
): Promise<Packet<boolean>> =>
  postRequest(`/v1/gold/userGoldInstrumentTask/upsert`, dto);

/**
 * gold arbitrage => ExecutionManagement
 * تنظیمات اجرا
 *
 * @async
 * @version 1.4.0
 */
export const getExecutionSettings = (): Promise<IExecutionSettings> =>
  getRequest(`/v1/gold/executionSettings`);

/**
 * gold arbitrage => ExecutionManagement
 * ثبت تنظیمات اجرا
 *
 * @param {IExecutionSettings} config
 * @async
 * @version 1.4.0
 */
export const setExecutionSettings = (
  config: IExecutionSettings,
): Promise<Packet<boolean>> =>
  postRequest(`/v1/gold/executionSettings`, config);

/**
 * gold arbitrage => ExecutionManagement
 * لیست اکانت‌ها
 *
 * @async
 * @version 1.4.0
 */
export const getAccounts = (): Promise<DropdownItem[]> =>
  getRequest(`/v1/gold/GetArbitrageUser`);
//-------------------------watchlist---------------------------

/**
 * gold arbitrage => WatchList
 * جدول واچ لیست موقعیت ها
 *
 * @param {GoldPositionWatchListSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getGoldPositionWatchListTable = (
  searchParam: GoldPositionWatchListSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<GoldPositionWatchListTableReport>> =>
  getTable<GoldPositionWatchListSearchParams, GoldPositionWatchListTableReport>(
    "/v1/gold/positionwatchList",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );
